import {
  type RefObject,
  type UIEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  type CallTranscriptSegment,
  resolveActiveSegmentIndex,
} from './call-transcript.utils';

// A smooth programmatic scroll emits a burst of scroll events; they only stop once it
// lands, so an in-flight scroll is one that keeps firing. Anything after this much
// quiet is the user.
const SCROLL_IDLE_MS = 150;

export const CALL_TRANSCRIPT_SEGMENT_INDEX_ATTRIBUTE = 'data-segment-index';

interface UseCallTranscriptAutoScrollInput {
  scrollRef: RefObject<HTMLDivElement | null>;
  segments: CallTranscriptSegment[];
  currentTimeSeconds: number;
  isPlaying: boolean;
  isTranscriptVisible: boolean;
  isSearchActive: boolean;
}

export const useCallTranscriptAutoScroll = ({
  scrollRef,
  segments,
  currentTimeSeconds,
  isPlaying,
  isTranscriptVisible,
  isSearchActive,
}: UseCallTranscriptAutoScrollInput) => {
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);
  const isProgrammaticScrollRef = useRef(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const activeSegmentIndex = useMemo(
    () => resolveActiveSegmentIndex(segments, currentTimeSeconds),
    [segments, currentTimeSeconds],
  );

  const keepProgrammaticScrollAlive = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    idleTimerRef.current = setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, SCROLL_IDLE_MS);
  }, []);

  useEffect(
    () => () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    },
    [],
  );

  // Distance the container must travel to centre the segment being played back.
  const getActiveSegmentOffset = useCallback(() => {
    const container = scrollRef.current;
    if (!container || activeSegmentIndex < 0) {
      return null;
    }

    const target = container.querySelector<HTMLElement>(
      `[${CALL_TRANSCRIPT_SEGMENT_INDEX_ATTRIBUTE}="${activeSegmentIndex}"]`,
    );
    if (!target) {
      return null;
    }

    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    return (
      targetRect.top - containerRect.top - container.clientHeight / 2 + targetRect.height / 2
    );
  }, [activeSegmentIndex, scrollRef]);

  const scrollToActiveSegment = useCallback(() => {
    const container = scrollRef.current;
    const offset = getActiveSegmentOffset();
    if (!container || offset === null || Math.abs(offset) < 1) {
      return;
    }

    isProgrammaticScrollRef.current = true;
    keepProgrammaticScrollAlive();
    container.scrollBy({ top: offset, behavior: 'smooth' });
  }, [getActiveSegmentOffset, keepProgrammaticScrollAlive, scrollRef]);

  useEffect(() => {
    if (!isAutoScrollEnabled || !isTranscriptVisible || !isPlaying || isSearchActive) {
      return;
    }
    scrollToActiveSegment();
  }, [
    isAutoScrollEnabled,
    isTranscriptVisible,
    isPlaying,
    isSearchActive,
    scrollToActiveSegment,
  ]);

  const pauseAutoScroll = useCallback(() => {
    isProgrammaticScrollRef.current = false;
    setIsAutoScrollEnabled(false);
  }, []);

  const handleScroll = useCallback(
    (_event: UIEvent<HTMLDivElement>) => {
      if (isProgrammaticScrollRef.current) {
        keepProgrammaticScrollAlive();
        return;
      }
      pauseAutoScroll();
    },
    [keepProgrammaticScrollAlive, pauseAutoScroll],
  );

  const resumeAutoScroll = useCallback(() => {
    setIsAutoScrollEnabled(true);
    scrollToActiveSegment();
  }, [scrollToActiveSegment]);

  return {
    activeSegmentIndex,
    handleScroll,
    // Wheel and touch are unambiguously the user, even mid-flight of our own scroll.
    handleUserScrollIntent: pauseAutoScroll,
    resumeAutoScroll,
    canResumeAutoScroll: !isAutoScrollEnabled,
  };
};
